import { EyeOff } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { CustomInput } from "@/components/forms/customInput"
import { EyeIcon } from "@/components/icons"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/features/auth/store/authStore"
import { consumeAuthReturnTo } from "@/features/auth/utils/authReturnTo"
import { SocialLoginButtons } from "../components/SocialLoginButtons"
import { useAuthForm } from "../hooks/useAuthForm"
import { type LoginFormData, loginSchema } from "../schemas/auth.schema"

export function LoginScreen() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const login = useAuthStore(state => state.login)
  const [showPassword, setShowPassword] = useState(false)

  const oauthErrorShown = useRef(false)

  // Show error toast from OAuth redirect (e.g. banned/suspended account)
  useEffect(() => {
    const error = searchParams.get("error")
    if (error && !oauthErrorShown.current) {
      oauthErrorShown.current = true
      toast.error(decodeURIComponent(error))
      navigate("/login", { replace: true })
    }
  }, [searchParams, navigate])

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    loginSchema,
    async (data: LoginFormData) => {
      const response = await login(data.email, data.password)

      // If email not verified, redirect to verify email page
      if (!response.user.emailVerified) {
        navigate("/verify-email", { state: { email: data.email } })
        return
      }

      // Smart routing based on user completion state
      if (!response.user.onboardingCompleted) {
        navigate("/onboarding/permissions")
      } else {
        // Return to the deep link that triggered login, if any.
        navigate(consumeAuthReturnTo() ?? "/")
      }
    },
  )

  const emailRegister = form.register("email")
  const passwordRegister = form.register("password")

  return (
    <AuthWrapper className="my-28">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6 px-5 sm:px-10 md:px-27">
          <div className="text-center mb-10 mt-5">
            <h2 className="text-2xl font-[500] text-primary font-base font-semibold">
              {t("login.title")}
            </h2>
          </div>

          <div className="space-y-2">
            <div className="space-y-5">
              <CustomInput
                label={t("login.emailLabel")}
                type="email"
                placeholder={t("login.emailPlaceholder")}
                required={false}
                {...emailRegister}
                error={form.formState.errors.email?.message as string | undefined}
                disabled={isSubmitting}
                onChange={e => {
                  emailRegister.onChange?.(e)
                  clearServerError()
                }}
                className="pb-1"
              />

              <div className="relative">
                <CustomInput
                  label={t("login.passwordLabel")}
                  type={showPassword ? "text" : "password"}
                  required={false}
                  placeholder={t("login.passwordPlaceholder")}
                  {...passwordRegister}
                  error={
                    (form.formState.errors.password?.message || serverError) as string | undefined
                  }
                  disabled={isSubmitting}
                  onChange={e => {
                    passwordRegister.onChange?.(e)
                    clearServerError()
                  }}
                  className="pb-1 pr-8"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center text-body cursor-pointer"
                  aria-label={
                    showPassword ? t("accessibility.hidePassword") : t("accessibility.showPassword")
                  }
                >
                  {showPassword ? (
                    <EyeOff size={17} className="text-primary" />
                  ) : (
                    <EyeIcon size={17} />
                  )}
                </button>
              </div>
            </div>

            <div className="text-center w-full pb-0 cursor-pointer flex items-end justify-end mt-0">
              <button
                type="button"
                className="text-[13px] text-body bg-transparent border-none p-0 me-1 leading-[25px] cursor-pointer"
                onClick={() => navigate("/forgot-password")}
              >
                {t("login.troubleSigning")}{" "}
                <span className="font-semibold">{t("login.resetPassword")}</span>
              </button>
            </div>

            <div className="py-2 mt-3.5">
              <Button
                type="submit"
                variant="primary-white"
                size="md"
                className="uppercase"
                isLoading={isSubmitting}
                fullWidth
              >
                Login
              </Button>
            </div>

            <SocialLoginButtons isLoading={isSubmitting} />
          </div>
        </div>

        <div className="text-center pt-12 w-full space-y-4.5">
          <p className="font-semibold text-[14px]">{t("login.dontHaveAccount")}</p>
          <div className="mx-auto sm:px-0 px-5">
            <Button
              type="button"
              variant="outline"
              size="md"
              className="uppercase w-full sm:w-[365px]  hover:!bg-transparent hover:!text-[#F2F0EB] hover:!border-[#F2F0EB]"
              onClick={() => navigate("/signup")}
            >
              Sign Up
            </Button>
          </div>
          <p className="text-[14px] mt-8 ms-2 text-body leading-[25px]">
            {" "}
            {t("login.termsIntro")}{" "}
            <a
              href="https://myrituality.com/terms/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold"
            >
              {t("login.terms")}
            </a>{" "}
            {t("login.and")}{" "}
            <a
              href="https://myrituality.com/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold"
            >
              {t("login.privacy")}
            </a>
            .
          </p>
        </div>
      </form>
    </AuthWrapper>
  )
}
